/**
 * `<SkillChecksPage>` — CHEATSHEET.app's skill-check adjudication page. The four blocks below
 * are sourced from the Lancer Core Rulebook (`the roll` + `bonuses` + `accuracy and difficulty`
 * from p.13-14's "SKILL CHECKS, ATTACKS, AND SAVES" / "BONUSES" / "ACCURACY AND DIFFICULTY"
 * sections; `DIFFICULT / RISKY / HEROIC` from p.45-46's "MAKING SKILL CHECKS" / "DIFFICULT,
 * RISKY, AND HEROIC ROLLS" / "TRYING AGAIN AND PUSHING IT" / "CONSEQUENCES" sections) —
 * paraphrased in our own words, page-cited per block, quoting verbatim only where the exact
 * phrasing is load-bearing ("cancel 1:1", "push it").
 *
 * **Source the pages as rendered images, not as extracted text**: `pdftotext` maps both the
 * accuracy and difficulty glyphs to the same replacement character, so a text-only extraction
 * cannot tell the two apart.
 *
 * The DIFFICULT/RISKY/HEROIC table's "Can be pushed further?" column is a per-tier breakdown
 * *derived* from the "Pushing it" paragraph below the table (which stays present in full), not
 * a line lifted from the book — DIFFICULT can retry-to-RISKY because it isn't itself a
 * RISKY/HEROIC tier and explicitly "stacks with either of the others".
 *
 * The four headings are `CheatsheetApp`'s + the render test's stable contract — don't rename them.
 */
export function SkillChecksPage() {
  return (
    <div className="gms-cheatsheet-page">
      <h2 className="gms-cheatsheet-page-title">ADJUDICATING SKILL CHECKS</h2>

      <section className="gms-cheatsheet-block">
        <h3>THE ROLL</h3>
        <ol className="gms-cheatsheet-steps">
          <li>Name the goal — what the character is trying to accomplish.</li>
          <li>
            GM sets the consequences of failure <strong>before</strong> the roll. No established consequences means no roll — the
            attempt just succeeds.
          </li>
          <li>Roll 1d20 + any bonuses that apply (see BONUSES, below).</li>
          <li>
            <strong>10+</strong> succeeds. <strong>9 or less</strong> fails, and the established consequences land.
          </li>
          <li>One roll per goal. Repeating a failed roll is pushing it — see DIFFICULT / RISKY / HEROIC, below.</li>
        </ol>
        <p className="gms-cheatsheet-callout">
          <strong>The target number is fixed at 10 — the GM cannot move it.</strong> What the GM controls is how hard getting there
          is, by declaring the roll DIFFICULT, RISKY, or HEROIC.
        </p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.13, p.45</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>BONUSES</h3>
        <ul className="gms-cheatsheet-list">
          <li>
            <strong>ACCURACY</strong> and <strong>DIFFICULTY</strong> — temporary, situational modifiers (see ACCURACY AND
            DIFFICULTY, below).
          </li>
          <li>
            <strong>STATISTIC BONUSES</strong> — from the pilot's <strong>triggers</strong>, their <strong>mech skills</strong>, or
            their <strong>GRIT</strong>. Only <strong>one</strong> statistic bonus applies to a given roll — they don't stack with
            each other.
          </li>
        </ul>
        <p>If none of these apply, it's a bare 1d20.</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.13</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>ACCURACY AND DIFFICULTY</h3>
        <ul className="gms-cheatsheet-list">
          <li>
            Each point of <strong>ACCURACY</strong> adds <strong>1d6</strong>. Each point of <strong>DIFFICULTY</strong> subtracts{" "}
            <strong>1d6</strong>.
          </li>
        </ul>
        {/* The two rules people get wrong at the table — pulled out of the list so they read at
            a glance instead of being one bullet among four. */}
        <div className="gms-cheatsheet-rules-grid">
          <div className="gms-cheatsheet-rule-card">
            <span className="gms-cheatsheet-rule-kicker">Cancel 1:1</span>
            <p>
              ACCURACY and DIFFICULTY cancel <strong>1:1</strong> — net them out before rolling anything.
            </p>
          </div>
          <div className="gms-cheatsheet-rule-card">
            <span className="gms-cheatsheet-rule-kicker">Take the highest, never sum</span>
            <p>Multiple dice of the <strong>same</strong> type never add together — roll them all and take the single highest result.</p>
          </div>
        </div>
        <ul className="gms-cheatsheet-list">
          <li>Net effect on a roll can never exceed <strong>±6</strong> (a single d6's ceiling).</li>
        </ul>
        <p className="gms-cheatsheet-example">
          E.g. 2 ACCURACY: roll 2d6, take the higher — a 3 and a 4 is +4, not +7. 2 ACCURACY + 1 DIFFICULTY nets to 1 ACCURACY: roll
          1d6. 1 ACCURACY + 1 DIFFICULTY cancel to nothing: no bonus die at all.
        </p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.13-14</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>DIFFICULT / RISKY / HEROIC</h3>
        <table className="gms-cheatsheet-table">
          <thead>
            <tr>
              <th>Declared</th>
              <th>Succeeds on</th>
              <th>Can be pushed further?</th>
              <th>Consequences land on</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">DIFFICULT</th>
              <td>10+ (as normal)</td>
              <td>Yes — a failed, not-yet-RISKY attempt can be retried as RISKY (stacks with DIFFICULT)</td>
              <td>9 or less — normal threshold, but the roll carries +1 DIFFICULTY</td>
            </tr>
            <tr>
              <th scope="row">RISKY</th>
              <td>10+</td>
              <td>Only with GM permission — becomes HEROIC</td>
              <td>anything under 20 — even on a success</td>
            </tr>
            <tr>
              <th scope="row">HEROIC</th>
              <td>20+ only</td>
              <td>No — a HEROIC roll can never be pushed further</td>
              <td>anything under 20</td>
            </tr>
          </tbody>
        </table>
        <p>
          DIFFICULT stacks with either of the others — the GM can call a roll DIFFICULT <em>and</em> RISKY, or DIFFICULT{" "}
          <em>and</em> HEROIC, together. RISKY and HEROIC are progression tiers rather than something stacked: pushing an
          already-RISKY roll (with GM permission) makes it HEROIC outright, not RISKY-and-HEROIC at once.
        </p>
        <p>
          A failed check can't just be retried under the same circumstances — that requires the narrative circumstances to have
          genuinely changed (time has passed, new gear, a different approach, additional help), or <strong>pushing it</strong>.{" "}
          <strong>Pushing it:</strong> failed a roll that isn't RISKY? Retry it under the same circumstances — but the retry
          becomes RISKY. Already RISKY? It can only push further if the GM allows it, and if so it becomes HEROIC. A HEROIC roll
          can never be pushed further.
        </p>
        <p>
          <strong>Consequence menu</strong> (set before the roll, not improvised after): <strong>Harm</strong> (damage or
          injury), <strong>Time</strong> (it takes longer), <strong>Resources</strong> (something's used up, lost, or exhausted),{" "}
          <strong>Collateral</strong> (someone or something else takes the hit), <strong>Position</strong> (worse footing for
          what comes next), <strong>Effect</strong> (the action lands, but weaker than intended).
        </p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.45-46</p>
      </section>
    </div>
  );
}
